import React from 'react';

import { motion } from 'framer-motion';
import { ArrowRight, Newspaper, Shirt, Wand2, Heart, Calendar, BookOpen, Palette, Rocket } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Home() {
    const features = [
        { 
            icon: Shirt, 
            title: 'Style Studio', 
            desc: 'Build outfits with your wardrobe pieces using drag-and-drop. Mix and match items on an interactive canvas and save to your Stylebook.', 
            color: 'from-suede-600 to-suede-400',
            link: '/style-studio'
        },
        { 
            icon: Wand2, 
            title: 'AI Studio', 
            desc: 'Generate AI fashion videos, remove backgrounds, and create stunning content. Virtual try-on powered by advanced AI models.', 
            color: 'from-terracotta-600 to-terracotta-400',
            link: '/ai-studio'
        },
        { 
            icon: Heart, 
            title: 'My Wardrobe', 
            desc: 'Your complete digital closet. Add items by photo, organize by category, build capsule wardrobes, and mark your favorites.', 
            color: 'from-olive-600 to-olive-400',
            link: '/my-wardrobe'
        },
        { 
            icon: BookOpen, 
            title: 'Stylebook', 
            desc: 'Create lookbooks and mood boards with your saved items and inspiration. Organize your style vision in one beautiful place.', 
            color: 'from-mud-700 to-mud-500',
            link: '/lookbooks'
        },
        { 
            icon: Calendar, 
            title: 'Outfit Calendar', 
            desc: 'Plan what to wear each day. Drag outfits onto your calendar, track your style patterns, and never repeat an outfit unintentionally.', 
            color: 'from-suede-700 to-olive-500',
            link: '/outfit-calendar'
        },
        { 
            icon: Newspaper, 
            title: 'Trend News', 
            desc: 'Stay ahead with viral fashion trends from TikTok, Instagram and Reddit. Daily curated style inspiration and trend alerts.', 
            color: 'from-olive-700 to-olive-500',
            link: '/news'
        },
        { 
            icon: Palette, 
            title: 'Picks of the Week', 
            desc: 'Curated fashion picks updated weekly. Discover trending pieces, editor selections, and seasonal must-haves all in one place.', 
            color: 'from-terracotta-700 to-terracotta-500',
            link: '/style-picks'
        },
        { 
            icon: Rocket, 
            title: 'Promoters', 
            desc: 'Discover featured brands and exclusive collaborations. Browse curated promoter showcases with special collections and offers.', 
            color: 'from-mud-600 to-mud-500',
            link: '/promoters'
        },
    ];


    // Helper function for CTA buttons
    const getFeatureCTA = (idx) => {
        const ctas = [
            "Build Outfits",
            "Create Content",
            "Open Wardrobe",
            "View Stylebook",
            "Plan Outfits",
            "See What's Trending",
            "Browse Picks",
            "Explore Brands"
        ];
        return ctas[idx];
    };

    return (
        <div className="min-h-screen overflow-x-hidden bg-[#faf9f7]">
            {/* Hero / Mission */}
            <section className="py-14 sm:py-16 lg:py-24 border-b border-stone-200">
                <div className="max-w-3xl mx-auto px-5 sm:px-6 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        <img 
                            src="/logo-original.PNG" 
                            alt="fashionDisc" 
                            className="w-28 h-28 sm:w-32 sm:h-32 mx-auto mb-6 object-contain"
                        />
                        <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-stone-400 mb-4">Your AI Fashion Companion</p>
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extralight text-stone-800 mb-5 sm:mb-6 leading-snug" style={{ fontFamily: 'Georgia, serif' }}>
                            What is 
                            <em className="font-light italic text-stone-600"> fashionDisc</em>?
                        </h2>
                        <p className="text-sm sm:text-base lg:text-lg text-stone-500 leading-relaxed max-w-2xl mx-auto">
                            fashionDisc is your complete AI-powered fashion companion that transforms how you organize and style your wardrobe. 
                            Build your digital closet with real clothes you own, create outfits on an interactive canvas with Style Studio, 
                            and generate AI fashion content with virtual try-on. Plan what to wear with your Outfit Calendar, 
                            curate your style vision in your Stylebook, and stay ahead with trending fashion news. 
                            Discover weekly style picks, explore featured brands, and get personalized recommendations from our 
                            AI Style Assistant. Your style journey, reimagined with AI.
                        </p>
                    </motion.div>
                </div>
            </section>
            
            {/* Features Section */}
            <section className="bg-white border-b border-stone-200">
                {/* Section Header */}
                <div className="py-12 sm:py-14 lg:py-20 px-5 sm:px-6 relative">
                    <div className="max-w-3xl mx-auto text-center">
                        <motion.div
                            initial={{ opacity: 0, y: 40, scale: 0.95 }}
                            whileInView={{ opacity: 1, y: 0, scale: 1 }}
                            viewport={{ once: false, margin: "-100px" }}
                            transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
                        >
                            <motion.p 
                                className="text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-stone-400 mb-4"
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                viewport={{ once: false }}
                                transition={{ duration: 0.5 }}
                            >
                                Features
                            </motion.p>
                            <motion.h2 
                                className="text-2xl sm:text-3xl lg:text-5xl font-extralight text-stone-800 mb-4 leading-snug" style={{ fontFamily: 'Georgia, serif' }}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: false }}
                                transition={{ duration: 0.6, delay: 0.1 }}
                            >
                                How <motion.span 
                                    className="italic font-light text-stone-600"
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    viewport={{ once: false }}
                                    transition={{ duration: 0.5, delay: 0.3, type: "spring", bounce: 0.4 }}
                                >fashionDisc</motion.span> Works
                            </motion.h2>
                            <motion.p 
                                className="text-stone-500 text-sm sm:text-base lg:text-lg max-w-2xl mx-auto"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: false }}
                                transition={{ duration: 0.6, delay: 0.4 }}
                            >
                                Build outfits with your wardrobe, create AI content, plan your style, and stay on trend. Everything you need in one place.
                            </motion.p>
                        </motion.div>
                    </div>
                </div>

                {/* Features Grid */}
                <div className="pb-14 sm:pb-16 lg:pb-24 px-5 sm:px-6 lg:px-12">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 lg:gap-6">
                        {features.map((feature, idx) => {
                            const iconBgColors = {
                                0: '#f0ebe5',  // suede-100
                                1: '#fde9e0',  // terracotta-100
                                2: '#e9f0db',  // olive-100
                                3: '#ede6de',  // mud-100
                                4: '#f0ebe5',  // suede-100
                                5: '#e9f0db',  // olive-100
                                6: '#fde9e0',  // terracotta-100
                                7: '#ede6de',  // mud-100
                            };
                            
                            return (
                                <motion.div 
                                    key={idx}
                                    className="p-5 sm:p-6 bg-[#faf9f7] border border-stone-200 rounded-2xl hover:shadow-md transition-all duration-300 group"
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: idx * 0.05, duration: 0.5 }}
                                    whileHover={{ y: -3 }}
                                >
                                    <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" style={{ backgroundColor: iconBgColors[idx] }}>
                                        <feature.icon className="w-6 h-6 text-stone-600" />
                                    </div>
                                    
                                    <h3 className="text-lg font-light mb-2" style={{ fontFamily: 'Georgia, serif', color: '#8a7560' }}>{feature.title}</h3>
                                    <p className="text-stone-500 text-sm leading-relaxed mb-4">{feature.desc}</p>
                                    
                                    {feature.link ? (
                                        <Link 
                                            to={feature.link} 
                                            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.1em] font-medium text-stone-600 hover:text-stone-800 group/link"
                                        >
                                            <span>{getFeatureCTA(idx)}</span>
                                            <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                                        </Link>
                                    ) : (
                                        <span className="inline-flex items-center gap-2 text-sm font-medium text-stone-400">
                                            <Wand2 className="w-4 h-4" />
                                            Always Available
                                        </span>
                                    )}
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

        </div>
    );
}
